/**
 * 複数選択値を、マスタに存在する正式名称を壊さずに分割する。
 *
 * 例:
 * - 正式名称: "XCB/XCF/XCM/XCR週報" → 1件のまま保持
 * - 複数値:   "内報/外報"           → 2件に分割
 */
export const normalizeReportTypeSelectionKey = (raw: unknown): string =>
  String(raw ?? '')
    .trim()
    .replace(/[ \t]*[／/][ \t]*/g, '/');

export const resolveKnownMultiSelectTokens = (
  raw: unknown,
  knownValues: readonly unknown[] | null | undefined,
  options: { onlyKnown?: boolean } = {}
): string[] => {
  const source = String(raw ?? '').trim();
  if (!source) return [];

  // 表示名は保持し、照合キーだけスラッシュ前後の半角スペースを除去する。
  // 同じキーが label/value の両方で渡された場合は、先に渡された表記を採用する。
  const knownValueByKey = new Map<string, string>();
  (Array.isArray(knownValues) ? knownValues : []).forEach((value) => {
    const displayValue = String(value ?? '').trim();
    const key = normalizeReportTypeSelectionKey(displayValue);
    if (!displayValue || !key || knownValueByKey.has(key)) return;
    knownValueByKey.set(key, displayValue);
  });
  const knownKeys = Array.from(knownValueByKey.keys()).sort(
    (a, b) => b.length - a.length
  );

  const segmentKnownValue = (value: string): string[] | null => {
    const normalized = normalizeReportTypeSelectionKey(value);
    if (!normalized) return [];

    const exact = knownValueByKey.get(normalized);
    if (exact) return [exact];

    // 長い正式名称から照合し、名称内のスラッシュと値の区切りを区別する。
    for (const key of knownKeys) {
      if (!normalized.startsWith(key)) continue;
      const remainder = normalized.slice(key.length);
      if (!remainder.startsWith('/')) continue;
      const rest = segmentKnownValue(remainder.slice(1));
      if (rest && rest.length > 0) {
        return [knownValueByKey.get(key) ?? key, ...rest];
      }
    }

    return null;
  };

  // "内報 / 外報" は複数値の保存形式として先に解釈する。
  // 各断片がマスタに存在しない場合だけ、空白を含む1つの正式名称として扱う。
  const explicitParts = source
    .split(/[ \t]+[/／][ \t]*|[/／][ \t]+|[|,、，]/)
    .map((token) => token.trim())
    .filter(Boolean);
  let resolved: string[] | null = null;
  let explicitPartResults: Array<string[] | null> = [];
  if (explicitParts.length > 1) {
    explicitPartResults = explicitParts.map(segmentKnownValue);
    if (explicitPartResults.every((tokens): tokens is string[] => tokens !== null)) {
      resolved = explicitPartResults.flat();
    }
  }

  if (resolved == null) {
    resolved = segmentKnownValue(source);
  }

  if (resolved == null && explicitParts.length > 1) {
    resolved = explicitParts.flatMap((part, index) => {
      const known = explicitPartResults[index];
      if (known) return known;
      return part
        .split(/[／/]/)
        .map((token) => token.trim())
        .filter(Boolean);
    });
  }

  // マスタに一致しない旧データは、従来どおり区切り文字で分割する。
  const tokens = (resolved ?? source
    .split(/[／/|,、，]/)
    .map((token) => token.trim())
    .filter(Boolean))
    .filter(
      (token, index, values) =>
        values.findIndex(
          (candidate) =>
            normalizeReportTypeSelectionKey(candidate) ===
            normalizeReportTypeSelectionKey(token)
        ) === index
    );

  // 週報分類のようなマスタ選択項目では、旧ロジックが残した分割片を
  // 選択値として復元しない。これにより、表示されない値が件数だけに
  // 含まれたり、そのまま再保存されたりすることを防ぐ。
  return options.onlyKnown
    ? tokens.filter((token) =>
        knownValueByKey.has(normalizeReportTypeSelectionKey(token))
      )
    : tokens;
};
